import { Box, DialogContent, DialogContentText, DialogTitle, ITheme, IconButton, Paper, Popover, Popper, SvgIcon, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip, Typography, alpha, lighten, useTheme } from '@mui/material'
import React, { useEffect, useId, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/reducers/store'
import { FundItem, FundItemContextualized } from '../../../models/Fund';
import NumberInput from '../../common/NumberInput';
import { useTranslation } from 'react-i18next';
import { removeFundToPortfolio, updateFundOfPortfolio } from '../../../store/actions/createPortfolio';
import RemoveIcon from '@mui/icons-material/Remove';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { ReactComponent as questionIcon } from '../../../assets/question.svg'
import FundAllocationItem from './FundAllocationItem';
import { loadListOfFunds } from '../../../store/actions/libraryOfFunds';
import ConfirmationForm from '../../common/ConfirmationForm';
import { Utilities } from '../../../utility/utils';

const FundAllocationTable: React.FC = () => {
    const funds = useAppSelector(state => state.libraryOfFundsReducer.allFunds);
    const portfolio = useAppSelector(state => state.createPortfolio.portfolio);
    const showAllocationError = useAppSelector(state => state.createPortfolio.showAllocationError);
    const [showRemoveItem, setShowRemoveItem] = useState<number | undefined>(undefined);
    const dispatch = useAppDispatch();
    const theme = useTheme() as ITheme;
    const anchorEl = useRef(null);
    const { t, i18n } = useTranslation();


    useEffect(() => {
        if (funds === undefined) {
            dispatch(loadListOfFunds());
        }
    }, [])

    if (portfolio === undefined || funds === undefined) {
        return <></>;
    }
    const onProportionChanged = (value: number | undefined, id: number) => {
        dispatch(updateFundOfPortfolio({ id: id, proportion: value ? value / 100 : 0 }));
    }
    const removeFund = (id: number) => {
        setShowRemoveItem(id);
    }
    const confirmRemoveFund = () => {
        dispatch(removeFundToPortfolio(showRemoveItem as number));
        setShowRemoveItem(undefined);
    }
    const cancelRemoveFund = () => {
        setShowRemoveItem(undefined);
    }


    const selectedFunds = portfolio.funds.map(sf => ({...funds.find(f => f.id === sf.id), proportion: sf.proportion})).filter(f => f !== undefined).map(f => ({ ...f, maxProposition: Utilities.getFundMaxProportion(portfolio.fundraisingTarget, f as FundItem) })).sort((a, b) => b.maxProposition - a.maxProposition) as FundItemContextualized[];
    const above20 = selectedFunds.filter(f => f.maxProposition > 0.2).sort();
    const exaclty20 = selectedFunds.filter(f => f.maxProposition == 0.2);
    const below20 = selectedFunds.filter(f => f.maxProposition < 0.2);
    const totalProportion = portfolio.funds.reduce((a, b) => a + b.proportion, 0);

    const renderItem = (fund: FundItemContextualized, index: number, color: string): React.ReactNode => {
        return <TableRow key={`fond-allocation-${fund.id}`} sx={{ borderBottom: "1px solid #B3B3B3", '&:last-child td, &:last-child th': { border: 0 } }} >
            <TableCell>{`${index + 1}`}.</TableCell>
            <TableCell>{fund.name}</TableCell>
            <TableCell>
                <Box display={"flex"} alignItems={"center"} justifyContent={"center"} gap={"5px"}>
                    {/* <NumberInput value={portfolio.funds[i].proportion * 100} slotProps={{
                    decrementButton: { hidden: true }, incrementButton: { hidden: true },
                    input: { style: { width: "100%", textAlign: "center" } }, root: { style: { width: "58px" } }
                }} onChange={(e, value) => onProportionChanged(value, f.id)}/> */}
                    <FundAllocationItem fund={fund} placeholder='0' value={fund.proportion * 100} onChange={(e, value) => onProportionChanged(value, fund.id)} style={{ width: "58px", textAlign: "center" }} disableMask backgroundColor={color} />
                    {/* <NumberInput placeholder='0' value={portfolio.funds[i].proportion * 100} onChange={(e, value) => onProportionChanged(value, f.id)} style={{ width: "58px" }} disableMask /> */}
                    <Typography>%</Typography>
                </Box>
            </TableCell>
            <TableCell>
                <Box flexGrow={1} textAlign={"center"}>
                    <Typography fontSize={"16px"} lineHeight={"19.5px"} fontWeight={"600"}>{`€${(fund.proportion * portfolio.fundraisingTarget).toLocaleString(i18n.language)}`}</Typography>
                </Box>
            </TableCell>
            <TableCell>
                <IconButton size='small' onClick={() => removeFund(fund.id)}>
                    <RemoveIcon fontSize='small' sx={{ color: theme.palette.primary.dark }} />
                </IconButton>
            </TableCell>
        </TableRow>
    }


    return <Box display={"flex"} flexDirection={"column"}>
        <Typography fontSize={"16px"} lineHeight={"19.5px"} fontWeight={"500"}>{t('CreatePortfolioEnterAllocationForEachFund')}</Typography>
        <TableContainer sx={{ width: "100%" }}>
            <Table>
                <TableBody>
                    {above20.length > 0 && <>
                        <TableRow>
                            <TableCell colSpan={5} sx={{ paddingTop: "10px" }}>
                                <Typography fontSize={"16px"} fontWeight={"600"} lineHeight={"19.5px"}>{t('CreatePortfolioAbove20Pc')} <Tooltip title={t('CreatePortfolioAbove20PcDesc')} placement='right'
                                    componentsProps={{
                                        tooltip: {
                                            style: {
                                                backgroundColor: lighten(theme.palette.leo.color10, 0.7),
                                                color: theme.palette.leo.color11,
                                                border: `1px solid ${theme.palette.leo.color11}`
                                            }
                                        }
                                    }}>
                                    <SvgIcon component={questionIcon} inheritViewBox sx={{ width: "10px", height: "11px" }} />
                                </Tooltip>
                                </Typography>
                            </TableCell>
                        </TableRow>
                        {above20.map((f, i) => (
                            renderItem(f, i, lighten(theme.palette.leo.color10, 0.7))
                        ))}
                    </>}
                    {exaclty20.length > 0 && <>
                        <TableRow>
                            <TableCell colSpan={5} sx={{ paddingTop: "10px" }}>
                                <Typography fontSize={"16px"} fontWeight={"600"} lineHeight={"19.5px"}>{t('CreatePortfolioUpTo20Pc')} <Tooltip title={t('CreatePortfolioUpTo20PcDesc')} placement='right'
                                    componentsProps={{
                                        tooltip: {
                                            style: {
                                                backgroundColor: lighten(theme.palette.leo.color3, 0.7),
                                                color: theme.palette.leo.color3,
                                                border: `1px solid ${theme.palette.leo.color3}`
                                            }
                                        }
                                    }}>
                                    <SvgIcon component={questionIcon} inheritViewBox sx={{ width: "10px", height: "11px" }} />
                                </Tooltip>
                                </Typography>
                            </TableCell>
                        </TableRow>
                        {exaclty20.map((f, i) => (
                            renderItem(f, i + above20.length, lighten(theme.palette.leo.color3, 0.7))
                        ))}
                    </>}
                    {below20.length > 0 && <>
                        <TableRow>
                            <TableCell colSpan={5} sx={{ paddingTop: "10px" }}>
                                <Typography fontSize={"16px"} fontWeight={"600"} lineHeight={"19.5px"}>{t('CreatePortfolioBelow20Pc')} <Tooltip title={t('CreatePortfolioBelow20PcDesc')} placement='right'
                                    componentsProps={{
                                        tooltip: {
                                            style: {
                                                backgroundColor: lighten(theme.palette.leo.color17, 0.7),
                                                color: theme.palette.leo.color17,
                                                border: `1px solid ${theme.palette.leo.color17}`
                                            }
                                        }
                                    }}>
                                    <SvgIcon component={questionIcon} inheritViewBox sx={{ width: "10px", height: "11px" }} />
                                </Tooltip>
                                </Typography>
                            </TableCell>
                        </TableRow>
                        {below20.map((f, i) => (
                            renderItem(f, i + exaclty20.length + above20.length, lighten(theme.palette.leo.color17, 0.7))
                        ))}
                    </>}
                    <TableRow>
                        <TableCell colSpan={2}><Typography fontSize={"16px"} lineHeight={"25px"} fontWeight={"400"}>{t('CreatePortfolioBottomTotal')}</Typography></TableCell>
                        <TableCell sx={{ textAlign: "center" }} ref={anchorEl}>
                            <Tooltip
                                PopperProps={{
                                    disablePortal: true,
                                }}
                                componentsProps={{
                                    arrow: {
                                        style: {
                                            color: theme.palette.leo.color2
                                        }
                                    },
                                    tooltip: {
                                        style: {
                                            backgroundColor: theme.palette.leo.color2
                                        }
                                    }
                                }}
                                open={showAllocationError}
                                placement='top-end'
                                disableFocusListener
                                disableHoverListener
                                disableTouchListener
                                title={
                                    <Box display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} padding={"12px"} gap={"12px"}>
                                        <Typography fontWeight={700} fontSize={"12px"} lineHeight={"18px"} color={theme.palette.common.white}>{t('CreatePortfolioUncompleteAllocationTitle')}</Typography>
                                        <Typography fontWeight={500} fontSize={"12px"} lineHeight={"18px"} color={theme.palette.common.white}>{t('CreatePortfolioUncompleteAllocationText')}</Typography>
                                    </Box>}
                                arrow={true}>
                                <div></div>
                            </Tooltip>
                            <Typography fontSize={"18px"} lineHeight={"25px"} fontWeight={"600"} color={showAllocationError ? theme.palette.leo.color2 : undefined}>
                                {(totalProportion * 100).toFixed(0)}% <SvgIcon component={questionIcon} inheritViewBox sx={{ width: "10px", height: "11px", marginBottom: "11px" }} />
                            </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                            <Typography fontSize={"18px"} lineHeight={"25px"} fontWeight={"600"} color={showAllocationError ? theme.palette.leo.color2 : undefined}>{`€${(totalProportion * portfolio.fundraisingTarget).toLocaleString(i18n.language)}`}</Typography>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell colSpan={2}><Typography fontSize={"16px"} lineHeight={"25px"} fontWeight={"300"} fontStyle={'initial'}>{t('CreatePortfolioBottomRemaining')}</Typography></TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                            <Typography fontSize={"18px"} lineHeight={"25px"} fontWeight={"400"} color={showAllocationError ? theme.palette.leo.color2 : undefined}>{(100 - totalProportion * 100).toFixed(0)}% <SvgIcon component={questionIcon} inheritViewBox sx={{ width: "10px", height: "11px", marginBottom: "11px" }} /></Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                            <Typography fontSize={"16px"} lineHeight={"25px"} fontWeight={"400"} color={showAllocationError ? theme.palette.leo.color2 : undefined}>{`€${((1 - totalProportion) * portfolio.fundraisingTarget).toLocaleString(i18n.language)}`}</Typography>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
        {showRemoveItem && <ConfirmationForm content={t('CreatePorfolioRemoveFundValidationText')} title={t('CreatePorfolioRemoveFundValidationTitle')}
            confirmAction={[
                { type: 'primary', title: t('CreatePortfolioButtonRemove'), action: confirmRemoveFund },
                { type: 'secondary', title: t('CreatePortfolioButtonCancel'), action: cancelRemoveFund }
            ]} onClose={cancelRemoveFund} />}
    </Box >
}

export default FundAllocationTable